/**
 * Getting a finished exercise into the database, including when the network is
 * not there.
 *
 * §4: "Tolerate connection loss. A dropped connection mid-session must not lose
 * the session data. Buffer locally, sync when able."
 *
 * HOW IT WORKS. Each exercise is written the moment it ends rather than at the
 * end of the session, so a session that dies halfway keeps everything it
 * actually did. A write that fails is put in localStorage and retried — at the
 * next exercise boundary, at the end of the session, and again the next time
 * the patient opens the app.
 *
 * WHY A RETRY IS SAFE. The server writes results keyed on
 * (run, position-in-run), so sending the same result twice rewrites one row
 * rather than adding a second copy of the same exercise to a patient's history.
 * That guarantee lives in the schema as a unique constraint, not in a comment.
 *
 * WHAT IS NOT BUFFERED. Starting a run needs the server — it is the server that
 * decides which session runs today (constraint 11), and no offline guess at
 * that is better than telling the patient to try again when they are back
 * online.
 */

import { api, type ExerciseResultPayload } from '../../api.js';

const BUFFER_KEY = 'pathways.therapy.pendingResults';

interface Pending {
  runId: string;
  payload: ExerciseResultPayload;
  /** For the doctor, if a stale buffer ever needs explaining. */
  queuedAt: string;
}

function readBuffer(): Pending[] {
  try {
    const raw = window.localStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Pending[]) : [];
  } catch {
    // A corrupt buffer is not worth taking the session down for.
    return [];
  }
}

function writeBuffer(pending: Pending[]): void {
  try {
    if (pending.length === 0) window.localStorage.removeItem(BUFFER_KEY);
    else window.localStorage.setItem(BUFFER_KEY, JSON.stringify(pending));
  } catch {
    // Storage full or blocked. The result is already lost at this point; there
    // is nothing useful to do about it here and nothing worth crashing for.
  }
}

async function send(item: Pending): Promise<void> {
  await api.post(`/therapy/runs/${item.runId}/results`, item.payload);
}

/**
 * Write one exercise's result. Returns true when the server has it.
 *
 * Never throws. A failed write is the patient's network, not the patient's
 * problem, and it must not interrupt a therapy session in progress — the
 * session carries on and the result goes in the buffer.
 */
export async function recordResult(
  runId: string,
  payload: ExerciseResultPayload,
): Promise<boolean> {
  const item: Pending = { runId, payload, queuedAt: new Date().toISOString() };
  try {
    await send(item);
    return true;
  } catch {
    writeBuffer([...readBuffer(), item]);
    return false;
  }
}

/**
 * Try everything still waiting. Safe to call at any time, including when the
 * buffer is empty.
 *
 * A 4xx means the server has REFUSED this result — a run that no longer exists,
 * an exercise the registry does not know. Retrying that forever would keep a
 * dead result in the buffer for the life of the browser, so it is dropped. A
 * 5xx or a network failure is kept, because that is the case this exists for.
 */
export async function flushPendingResults(): Promise<{ sent: number; kept: number }> {
  const pending = readBuffer();
  if (pending.length === 0) return { sent: 0, kept: 0 };

  const keep: Pending[] = [];
  let sent = 0;

  for (const item of pending) {
    try {
      await send(item);
      sent += 1;
    } catch (error) {
      const status = (error as { status?: number }).status;
      if (typeof status === 'number' && status >= 400 && status < 500) continue;
      keep.push(item);
    }
  }

  writeBuffer(keep);
  return { sent, kept: keep.length };
}

/** How many results are still waiting, for a quiet note on the close-out. */
export function pendingResultCount(): number {
  return readBuffer().length;
}
